import { createContext, useContext, useState, useEffect } from 'react';

// Extensible sport configuration
export const SPORTS_CONFIG = {
  football: {
    key: 'football',
    label: 'Football',
    icon: '⚽',
    route: '/football'
  },
  cricket: {
    key: 'cricket', 
    label: 'Cricket',
    icon: '🏏',
    route: '/cricket'
  },
  tennis: {
    key: 'tennis',
    label: 'Tennis',
    icon: '🎾',
    route: '/tennis'
  },
  golf: {
    key: 'golf',
    label: 'Golf',
    icon: '⛳',
    route: '/golf'
  }
};

// Valid sport modes: 'all' or any key from SPORTS_CONFIG
const VALID_MODES = ['all', ...Object.keys(SPORTS_CONFIG)];
const STORAGE_KEY = 'sportMode';
const DEFAULT_MODE = 'all';

const SportModeContext = createContext(null);

export function SportModeProvider({ children }) {
  const [sportMode, setSportModeState] = useState(() => {
    // Check if this is a fresh session (not coming from /football or /cricket redirect)
    const wasSetThisSession = sessionStorage.getItem('sportModeSet');
    
    if (!wasSetThisSession) {
      // Fresh session - reset to 'all' mode
      localStorage.setItem(STORAGE_KEY, DEFAULT_MODE);
      return DEFAULT_MODE;
    }
    
    // Coming from a redirect - use stored mode
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_MODES.includes(stored)) {
      return stored;
    }
    return DEFAULT_MODE;
  });

  // Persist to localStorage when mode changes
  const setSportMode = (mode) => {
    if (VALID_MODES.includes(mode)) {
      localStorage.setItem(STORAGE_KEY, mode);
      setSportModeState(mode);
    } else {
      console.warn(`Invalid sport mode: ${mode}. Valid modes: ${VALID_MODES.join(', ')}`);
    }
  };

  // Helper to check if a sport should be visible
  const isSportVisible = (sportKey) => {
    if (sportMode === 'all') return true;
    return sportMode === sportKey;
  };

  // Helper to filter items by sport
  const filterBySport = (items, sportKeyField = 'sportKey') => {
    if (sportMode === 'all') return items;
    return items.filter(item => item[sportKeyField] === sportMode);
  };

  const value = {
    sportMode,
    setSportMode,
    isSportVisible,
    filterBySport,
    isAllSports: sportMode === 'all',
    currentSportConfig: sportMode !== 'all' ? SPORTS_CONFIG[sportMode] : null,
    availableSports: SPORTS_CONFIG
  };

  return (
    <SportModeContext.Provider value={value}>
      {children}
    </SportModeContext.Provider>
  );
}

export function useSportMode() {
  const context = useContext(SportModeContext);
  if (!context) {
    throw new Error('useSportMode must be used within a SportModeProvider');
  }
  return context;
}

// Component to set sport mode from URL and redirect
export function SportModeRedirect({ sport }) {
  useEffect(() => {
    // Set localStorage directly (synchronous)
    if (VALID_MODES.includes(sport)) {
      localStorage.setItem(STORAGE_KEY, sport);
      // Mark that we're coming from a sport mode redirect
      sessionStorage.setItem('sportModeSet', 'true');
    }
    // Use replace to avoid back button issues
    window.location.replace('/');
  }, [sport]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B101B' }}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
        <p className="text-white/60">Setting up {SPORTS_CONFIG[sport]?.label || sport} mode...</p>
      </div>
    </div>
  );
}
